import type { ListBoxProps, MultiSelectProps } from '@workramp/ui';
import { ListBox, MultiSelect, Option, OptionGroup } from '@workramp/ui';

interface Item {
  key: string;
  name: string;
}

interface PrimaryTier extends Item {
  options: Item[];
}

export interface TieredSelectProps extends Omit<MultiSelectProps, 'label' | 'title' | 'children'> {
  primaryTitle: string;
  secondaryTitle: string;
  options: PrimaryTier[];
  selection: { primaryKey: string; secondaryKey: string };
  onSelectionChange: (selection: { primaryKey: string; secondaryKey: string }) => void;
}

export function TieredSelect({
  primaryTitle,
  secondaryTitle,
  options,
  selection,
  onSelectionChange,
  minWidth = 240,
  ...props
}: TieredSelectProps) {
  const selectedKeys = Object.values(selection);

  const handleSelectionChange: ListBoxProps<Item>['onSelectionChange'] = (keys) => {
    if (keys !== 'all') {
      const changedKeys = new Set(keys);
      // Remove current selection to find changed key
      selectedKeys.forEach((key) => changedKeys.delete(key));

      const newPrimary = options.find((option) => changedKeys.has(option.key));
      const newSecondaryKey = newPrimary?.options[0]?.key;
      if (newPrimary && newSecondaryKey) {
        // primary key changed, reset secondary key
        onSelectionChange({ primaryKey: newPrimary.key, secondaryKey: newSecondaryKey });
        return;
      }

      const samePrimary = options.find((option) => option.key === selection.primaryKey);
      const newSecondary = samePrimary?.options.find((option) => changedKeys.has(option.key));
      if (newSecondary) {
        onSelectionChange({ primaryKey: selection.primaryKey, secondaryKey: newSecondary.key });
      }
    }
  };

  const selectedPrimaryOption = options.find((option) => option.key === selection.primaryKey);
  const secondaryOptions = options.find((option) => option.key === selection.primaryKey)?.options || [];

  return (
    <MultiSelect
      label={selectedPrimaryOption?.name ?? ''}
      title={primaryTitle}
      minWidth={minWidth}
      data-testid="tiered-select-button"
      {...props}
    >
      <ListBox
        aria-label={primaryTitle}
        selectedKeys={selectedKeys}
        selectionMode="multiple"
        onSelectionChange={handleSelectionChange}
      >
        <OptionGroup title={primaryTitle}>
          {options.map((option) => (
            <Option key={option.key}>{option.name}</Option>
          ))}
        </OptionGroup>
        <OptionGroup aria-label={secondaryTitle}>
          {secondaryOptions.map((option) => (
            <Option key={option.key}>{option.name}</Option>
          ))}
        </OptionGroup>
      </ListBox>
    </MultiSelect>
  );
}
